import { useState } from "react";

import { XIcon } from "../../shared/Icons";
import { TYPE_LABEL, ghostBtn, inp, lbl, type Field, type FieldType } from "../SelfService";
import { FieldInput } from "./FieldInput";
import { slug, uniqueKey } from "./layout";

// Propriedades de UM campo editável (extraído do antigo FieldsPanel).
// Auto-slug: a chave acompanha o rótulo enquanto for "automática" (vazia,
// campo_N, ou igual ao slug do rótulo atual); editar a chave à mão encerra isso.

export const optId = () => Math.random().toString(36).slice(2, 8);
const chipBtn: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 8px", borderRadius: 6, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)", cursor: "pointer" };

export const chipsOf = (f: Field): string[] => [
  ...(f.type === "multi" ? (f.options ?? []).map((_, i) => `{{ campo.${f.key}_${String(i + 1).padStart(2, "0")} }}`) : [`{{ campo.${f.key} }}`]),
  ...((f.type === "select" || f.type === "multi") && f.other ? [`{{ campo.${f.key}_outro }}`] : []),
];

export function FieldEditor({ f, taken, onChange, onRemove, onInsert }: {
  f: Field;
  taken: Set<string>;                      // chaves dos OUTROS campos do global
  onChange: (patch: Partial<Field>) => void;
  onRemove: () => void;
  onInsert: ((chip: string) => void) | null;  // null = editor da sessão não está aberto
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const auto = !f.key || f.key.startsWith("campo_") || f.key === slug(f.label);
  const chip = (c: string) => {
    onInsert?.(c);
    navigator.clipboard?.writeText(c).catch(() => {});
    setCopied(c);
    setTimeout(() => setCopied(null), 1200);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {chipsOf(f).map((c) => (
          <button key={c} title={onInsert ? "Inserir no cursor do editor e copiar" : "Abra a sessão para inserir no editor (copia mesmo assim)"}
            onClick={() => chip(c)} style={{ ...chipBtn, opacity: onInsert ? 1 : 0.6 }}>{copied === c ? "copiado ✓" : c.replace(/[{} ]/g, "")}</button>
        ))}
        <span style={{ flex: 1 }} />
        <button title="Remover campo" onClick={onRemove} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={12} /></button>
      </div>
      <div><label style={lbl}>Rótulo (o cliente vê)</label>
        <input style={inp} value={f.label} placeholder="Tipo de conversação"
          onChange={(e) => onChange({ label: e.target.value, key: auto ? uniqueKey(slug(e.target.value) || f.key, taken) : f.key })} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div><label style={lbl}>Chave do chip</label>
          <input style={{ ...inp, fontFamily: "var(--font-mono)" }} value={f.key} onChange={(e) => onChange({ key: uniqueKey(slug(e.target.value), taken) })} /></div>
        <div><label style={lbl}>Tipo</label>
          <select style={inp} value={f.type} onChange={(e) => onChange({ type: e.target.value as FieldType, options: e.target.value === "select" || e.target.value === "multi" ? (f.options ?? [{ id: optId(), label: "Sim", text: "" }, { id: optId(), label: "Não", text: "" }]) : f.options, default: undefined })}>
            {(Object.keys(TYPE_LABEL) as FieldType[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select></div>
      </div>
      <div><label style={lbl}>Ajuda para o cliente (opcional)</label>
        <input style={inp} value={f.description ?? ""} onChange={(e) => onChange({ description: e.target.value })} placeholder="Como o agente deve soar na conversa" /></div>
      {(f.type === "text" || f.type === "textarea") && (
        <div><label style={lbl}>Limite de caracteres</label>
          <input type="number" min={1} style={{ ...inp, width: 160 }} value={f.limit ?? ""} onChange={(e) => onChange({ limit: e.target.value ? Number(e.target.value) : null })} placeholder="sem limite" /></div>
      )}
      {f.type === "bool" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11, color: "var(--ink-soft)" }}>Texto injetado no prompt em cada caso (pode ficar vazio)</div>
          <div><label style={lbl}>Quando "Sim"</label>
            <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.yes_text ?? ""} onChange={(e) => onChange({ yes_text: e.target.value })} placeholder="Você aceita veículos na troca: colete marca, modelo, ano e km." /></div>
          <div><label style={lbl}>Quando "Não"</label>
            <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.no_text ?? ""} onChange={(e) => onChange({ no_text: e.target.value })} placeholder="A loja não aceita veículos na troca." /></div>
        </div>
      )}
      {f.type === "list" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Máx. itens</span>
          <input type="number" min={1} style={{ ...inp, width: 70 }} value={f.max_items ?? ""} onChange={(e) => onChange({ max_items: e.target.value ? Number(e.target.value) : null })} placeholder="livre" />
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>o cliente digita os itens; o chip vira uma lista (um item por linha)</span>
        </div>
      )}
      {f.type === "hours" && (
        <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>O cliente marca os dias e as faixas de horário; o chip vira a frase "De segunda a sexta das 08:00 às 11:00 e das 13:00 às 18:00. Sábado das 09:00 às 13:00."</div>
      )}
      {f.type === "faq" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div><label style={lbl}>Máx. de perguntas</label>
            <input type="number" min={1} style={inp} value={f.max_items ?? ""} onChange={(e) => onChange({ max_items: e.target.value ? Number(e.target.value) : null })} placeholder="livre" /></div>
          <div><label style={lbl}>Caracteres da pergunta</label>
            <input type="number" min={1} style={inp} value={f.q_limit ?? ""} onChange={(e) => onChange({ q_limit: e.target.value ? Number(e.target.value) : null })} placeholder="300" /></div>
          <div><label style={lbl}>Caracteres da resposta</label>
            <input type="number" min={1} style={inp} value={f.a_limit ?? ""} onChange={(e) => onChange({ a_limit: e.target.value ? Number(e.target.value) : null })} placeholder="sem limite" /></div>
        </div>
      )}
      {/* Padrão: mesmo input que o cliente vê no portal; vale enquanto ele não responder. */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Padrão (opcional) · o que vale até o cliente preencher</label>
          {f.default != null && <button onClick={() => onChange({ default: undefined })} style={{ ...ghostBtn, padding: "2px 8px", fontSize: 11 }}>limpar</button>}
        </div>
        <FieldInput f={f} value={f.default} onChange={(v) => onChange({ default: v })} />
      </div>
      {f.type === "slider" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {(["min", "max", "step"] as const).map((k) => (
              <div key={k}><label style={lbl}>{k === "step" ? "Passo" : k === "min" ? "Início" : "Fim"}</label>
                <input type="number" style={inp} value={(f as any)[k] ?? ""} onChange={(e) => onChange({ [k]: e.target.value === "" ? undefined : Number(e.target.value) } as any)} placeholder={k === "min" ? "0" : k === "max" ? "10" : k === "step" ? "1" : ""} /></div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div><label style={lbl}>Rótulo do início</label><input style={inp} value={f.min_label ?? ""} onChange={(e) => onChange({ min_label: e.target.value })} placeholder="Robô" /></div>
            <div><label style={lbl}>Rótulo do fim</label><input style={inp} value={f.max_label ?? ""} onChange={(e) => onChange({ max_label: e.target.value })} placeholder="Engraçado" /></div>
          </div>
        </>
      )}
      {(f.type === "select" || f.type === "multi") && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11, color: "var(--ink-soft)" }}>Opção (o cliente vê) · texto injetado no prompt (vazio = a própria opção)</div>
          {(f.options ?? []).map((o, oi) => (
            <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1fr 20px", gap: 6, alignItems: "start" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <input style={inp} value={o.label} onChange={(e) => onChange({ options: f.options!.map((x, k) => (k === oi ? { ...x, label: e.target.value } : x)) })} placeholder="Usa intimidade" />
                <textarea rows={1} style={{ ...inp, resize: "vertical", minHeight: 32 }} value={o.text} onChange={(e) => onChange({ options: f.options!.map((x, k) => (k === oi ? { ...x, text: e.target.value } : x)) })} placeholder="Trate o cliente com proximidade, como um amigo de longa data." />
              </div>
              <button onClick={() => onChange({ options: f.options!.filter((_, k) => k !== oi) })} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", marginTop: 6 }}><XIcon size={12} /></button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => onChange({ options: [...(f.options ?? []), { id: optId(), label: "", text: "" }] })} style={ghostBtn}>+ Opção</button>
            <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Exibir como</span>
            <select style={{ ...inp, width: 130 }} value={f.display ?? "cards"} onChange={(e) => onChange({ display: e.target.value === "dropdown" ? "dropdown" : undefined })}>
              <option value="cards">Cartões</option>
              <option value="dropdown">Lista suspensa</option>
            </select>
            {f.type === "multi" && (
              <>
                <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Máx. escolhas</span>
                <input type="number" min={1} style={{ ...inp, width: 70 }} value={f.max_choices ?? ""} onChange={(e) => onChange({ max_choices: e.target.value ? Number(e.target.value) : null })} placeholder="livre" />
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-soft)" }}>
              <input type="checkbox" checked={!!f.other} onChange={(e) => onChange({ other: e.target.checked || undefined })} /> Opção "Outros" com texto livre
            </label>
            {f.other && (
              <>
                <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Limite de caracteres</span>
                <input type="number" min={1} style={{ ...inp, width: 90 }} value={f.other_limit ?? ""} onChange={(e) => onChange({ other_limit: e.target.value ? Number(e.target.value) : null })} placeholder="sem limite" />
                <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>o texto vai no chip {f.key}_outro</span>
              </>
            )}
          </div>
          {f.type === "multi" && <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>chips: um por opção ({f.key}_01, {f.key}_02…); opção não marcada = chip vazio</span>}
        </div>
      )}
    </div>
  );
}
